import { Suspense, lazy, useState } from 'react';
import './App.css';
import { trackGaEvent } from './analytics';
import DonateSection from './DonateSection';

const SITE_TITLE = "Coccam's Razor";
const PUBESOCKS_URL = 'https://pubesocks.com';

const waitlistEndpoint =
  process.env.REACT_APP_WAITLIST_URL?.trim() || '/api/waitlist';
const RazorViewer = lazy(() => import('./RazorViewer'));

const HOW_IT_WORKS = [
  {
    step: '1',
    title: 'Pick up the handle',
    bullets: [
      'There’s ONE handle. We didn’t bolt on seventeen ergonomic curves like some lunatic at Gillette.',
      'If you can’t grip it, that’s a you problem. I’m not your life coach.',
    ],
    aside: 'No assembly. There’s nothing TO assemble. Jesus.',
  },
  {
    step: '2',
    title: 'You got hair down there',
    bullets: [
      'Pubic hair. Say it out loud. What are you, twelve?',
      'One blade. You’re not landing a jet. Stop buying razors with a pivot ball like it’s NASA equipment.',
    ],
    aside: 'Blade count: 1. We\'re not negotiating.',
  },
  {
    step: '3',
    title: 'Shave',
    bullets: [
      'Go slow. Everybody wants to rush everything — shave, marriage, podcasts — and then they wonder why they’re bleeding.',
      'Rinse it off. Look at that. One blade did the job. You’re welcome.',
    ],
    aside: 'Takes less time than you complaining about it',
  },
  {
    step: '4',
    title: 'Mail it to Pubesocks',
    bullets: [
      'Yeah. The sock people. You already know the deal — bag it, ship it, stop flushing civilization down the toilet.',
      'They knit socks out of it. You get a discount. Everybody wins except your dignity, and that ship sailed. Go buy some socks.',
    ],
    aside: 'Full-circle nonsense™',
  },
];

const VS_STANDARD = [
  {
    feature: 'Blade count',
    standard: 'Five blades and a commercial where a hairy guy kayaks',
    coccams: 'If you\'d like to sponsor a commercial, we\'ll personally shave your junk for a year',
  },
  {
    feature: 'Philosophy',
    standard: '“Innovation” (translation: more plastic)',
    coccams: '“Stop it.” — Occam, assuming he wasn\'t bald',
  },
  {
    feature: 'Moisture strip',
    standard: 'Peels off in a week like a bad sunburn',
    coccams: 'Use soap. You have running water.',
  },
  {
    feature: 'Pubesocks synergy',
    standard: 'Zero. You’re just clogging the drain like an animal.',
    coccams: 'Trim, mail, discounted socks. It’s a whole deranged ecosystem.',
  },
  {
    feature: 'Meetings',
    standard: 'Probably twelve VP’s named Chad',
    coccams: 'One meeting. Seymour gave a keynote demonstration. We left.',
  },
];

function App() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifyError, setNotifyError] = useState(null);
  const [showInteractiveViewer, setShowInteractiveViewer] = useState(false);
  const [trackedModelInteraction, setTrackedModelInteraction] = useState(false);

  function handleRevealViewer() {
    if (showInteractiveViewer) return;
    setShowInteractiveViewer(true);
    trackGaEvent('model_view_requested', { model_name: 'coccams_razor_3d' });
  }

  function handleModelInteract() {
    if (trackedModelInteraction) return;
    setTrackedModelInteraction(true);
    trackGaEvent('model_view_interaction', { model_name: 'coccams_razor_3d' });
  }

  async function handleNotify(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setNotifyError(null);
    setLoading(true);
    try {
      const res = await fetch(waitlistEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      let data = {};
      try {
        data = await res.json();
      } catch {
        /* non-JSON response */
      }
      if (!res.ok) {
        throw new Error(data.error || `Something went wrong (${res.status})`);
      }
      setSubmitted(true);
      setEmail('');
      trackGaEvent('generate_lead', { method: 'waitlist' });
    } catch (err) {
      setNotifyError(err.message || 'The internet broke. Try again, I guess.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="landing">
      <header className="site-header">
        <span className="site-brand">{SITE_TITLE}</span>
        <a
          className="header-link"
          href={PUBESOCKS_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackGaEvent('click', { link_url: PUBESOCKS_URL, link_text: 'Pubesocks' })}
        >
          Yeah obviously it&apos;s a Pubesocks thing. Don&apos;t make it weird. →
        </a>
      </header>

      <main className="main">
        <section className="hero" aria-labelledby="hero-heading">
          <p className="hero-eyebrow">
            We spelled Occam wrong on purpose · that's the whole thing.
          </p>
          <h1 id="hero-heading">
            It&apos;s a razor. For your pubes. That&apos;s the whole pitch.
          </h1>
          <p className="hero-lede">
            Listen. You walk into CVS, there&apos;s a wall of plastic razors with seventeen blades and a
            moisture strip that won't stay on. We can do better than this.
            
          </p>
          <div className="hero-visual">
            {showInteractiveViewer ? (
              <>
                <Suspense
                  fallback={
                    <img
                      src={`${process.env.PUBLIC_URL}/hero-banner.png`}
                      alt="Coccam's Razor banner"
                      width={1200}
                      height={675}
                      className="hero-banner"
                    />
                  }
                >
                  <RazorViewer onInteract={handleModelInteract} />
                </Suspense>
                <p className="hero-caption">
                  Move your mouse to bob the blade and shave the patch. Drag to orbit, scroll to zoom.
                </p>
              </>
            ) : (
              <>
                <img
                  src={`${process.env.PUBLIC_URL}/hero-banner.png`}
                  alt="Coccam's Razor — one blade on marble, because we're not animals"
                  width={1200}
                  height={675}
                  className="hero-banner"
                />
                <button
                  type="button"
                  className="btn-secondary hero-demo-btn"
                  onClick={handleRevealViewer}
                >
                  I need to see this in action
                </button>
                <p className="hero-caption">
                  Start with the glam shot. Hit the button if you need the live instructional demo.
                </p>
              </>
            )}
          </div>
          <a className="btn-primary hero-cta" href="#sign-up">
            Put me on the list before I talk myself out of this
          </a>
        </section>

        <section className="stats" aria-label="Ridiculous metrics">
          <div className="stat">
            <span className="stat-value">1</span>
            <span className="stat-label">Blade. ONE.</span>
          </div>
          <div className="stat">
            <span className="stat-value">0</span>
            <span className="stat-label">Pivoting ball-joint bullshit</span>
          </div>
          <div className="stat">
            <span className="stat-value">∞</span>
            <span className="stat-label">Times I&apos;ve explained Pubesocks at Thanksgiving</span>
          </div>
        </section>

        <section className="origin" aria-labelledby="origin-heading">
          <p className="section-label">Why this exists</p>
          <h2 id="origin-heading">From the makers of Pubesocks - now we receive gallons of pubic hair every week. We're leaning into the market, deal with it.</h2>
          <p>
            <a href={PUBESOCKS_URL} target="_blank" rel="noopener noreferrer">
              Yep, Pubesocks.
            </a>{' '}
            I know how it sounds. Turns out America wants socks made from… look, you mail them pubic hair,
            their moms knit socks, you get a discount if you donate your shavings. Fine. It&apos;s weird, but it&apos;s honest. Our top
            contributors kept showing up with vats of fuzz going, &ldquo;Sorry it&apos;s
            not trimmed, I got lazy.&rdquo; LAZY? You&apos;re already mailing pubes through the U.S. Postal
            Service! You can take some damn time to sort out the split-ends
          </p>
          <h3>So we made a razor. Just for you.</h3>
          <p>
            One blade. To enable you freaks to contribute to the economy a little.

          </p>
     
          <blockquote className="pull-quote">
            &ldquo;The socks proved America’s insane. The razor just makes the insanity… tidier.&rdquo;
            <cite>— Seymour Bush, and I wish that was some kind of joke</cite>
          </blockquote>
        </section>

        <section className="how" aria-labelledby="how-heading">
          <p className="section-label">How it works</p>
          <h2 id="how-heading">Four steps, not gonna dance around it.</h2>
          <p className="section-intro">
            If you can operate a spoon without injuring yourself, you can use this razor. If you can&apos;t,
            maybe sit this one out and call in your mom.
          </p>
          <ol className="steps">
            {HOW_IT_WORKS.map((item) => (
              <li key={item.step} className="step-card">
                <span className="step-num">Step {item.step}</span>
                <h3>{item.title}</h3>
                <ul>
                  {item.bullets.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
                <p className="step-aside">{item.aside}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="compare" aria-labelledby="compare-heading">
          <p className="section-label">Why it&apos;s better</p>
          <h2 id="compare-heading">Your drugstore razor is a scam and you know it</h2>
          <p className="section-intro">
            They keep adding blades like it&apos;s a arms race. Four blades, five blades, next they&apos;ll
            sell you a razor with a bottle opener and a Bluetooth speaker. We went the other way. The simplest possible device to shave your junk.
            Have a soul for christ's sake.
          </p>
          <div className="compare-table-wrap">
            <table className="compare-table">
              <thead>
                <tr>
                  <th scope="col">Category</th>
                  <th scope="col">Standard shaver</th>
                  <th scope="col">Coccam&apos;s Razor</th>
                </tr>
              </thead>
              <tbody>
                {VS_STANDARD.map((row) => (
                  <tr key={row.feature}>
                    <th scope="row">{row.feature}</th>
                    <td>{row.standard}</td>
                    <td className="compare-winner">{row.coccams}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="fine-print">
            * Not FDA approved. Not barber approved. Not approved by my wife when I explained the $50k business loan over dinner.
          </p>
        </section>

        <section className="notify" id="sign-up" aria-labelledby="notify-heading">
          <p className="section-label">Sign me up</p>
          <h2 id="notify-heading">You're confounded, but curious. We get it. Give us your email.</h2>
          <p>
            We&apos;ll tell you when this thing ships. You&apos;ll get launch news, Pubesocks credit drama,
            and probably emails you didn&apos;t ask for. We&apos;re not gonna spam you — we&apos;re too busy
            arguing about blade count and whether or not we'll continue to allow the curly-haired.
          </p>
          <form className="notify-form" onSubmit={handleNotify}>
            <label htmlFor="notify-email" className="visually-hidden">
              Email address
            </label>
            <input
              id="notify-email"
              type="email"
              name="email"
              autoComplete="email"
              placeholder="your.email@whatever.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Hang on…' : 'Yeah, sign me up'}
            </button>
          </form>
          {notifyError ? (
            <p className="notify-error" role="alert">
              {notifyError}
            </p>
          ) : null}
          {submitted ? (
            <p className="notify-success" role="status">
              You&apos;re on the list. Go grow some inventory for the sock people.
            </p>
          ) : null}
        </section>

        <DonateSection />
      </main>

      <footer className="site-footer">
        <p>
          © {new Date().getFullYear()} {SITE_TITLE} ·{' '}
          <a href={PUBESOCKS_URL} target="_blank" rel="noopener noreferrer">
            Pubesocks
          </a>{' '}
          adjacent · Not medical advice · I&apos;m yelling about pubes on the internet again
        </p>
      </footer>
    </div>
  );
}

export default App;
